import React from "react";
import { Edit2, Trash2, Search } from "lucide-react";
import { Button } from "../ui/Button";
import type { SunbedMembershipPlanApi } from "../../services/sunbeds";
import { useAuth } from "../../auth/AuthContext";

interface MembershipPlansTableProps {
  plans: SunbedMembershipPlanApi[];
  isLoading: boolean;
  indexOfFirstItem: number;
  onEdit: (plan: SunbedMembershipPlanApi) => void;
  onDelete: (id: number) => void;
}

function entitlementLabel(plan: SunbedMembershipPlanApi): string {
  if (plan.entitlement === "unlimited") {
    return plan.max_session_minutes
      ? `Unlimited · max ${plan.max_session_minutes}m/session`
      : "Unlimited";
  }
  const minutes = plan.minutes_per_period ?? 0;
  return `${minutes} min / period${plan.rollover ? " · rollover" : ""}`;
}

function intervalLabel(plan: SunbedMembershipPlanApi): string {
  const count = plan.interval_count || 1;
  const unit = plan.interval === "week" ? "week" : plan.interval === "year" ? "year" : "month";
  return count === 1 ? `Every ${unit}` : `Every ${count} ${unit}s`;
}

export const MembershipPlansTable: React.FC<MembershipPlansTableProps> = ({
  plans,
  isLoading,
  indexOfFirstItem,
  onEdit,
  onDelete,
}) => {
  const { user } = useAuth();
  const currencySymbol =
    user?.company?.currency_symbol || user?.company?.currency?.currency_symbol || "£";

  return (
    <div className="flex-1 overflow-y-auto relative">
      <table className="w-full">
        <thead className="sticky top-0 z-10 bg-[#885ABB] border-b border-gray-100">
          <tr className="grid grid-cols-12 gap-4 text-sm font-bold text-white tracking-wide px-6 py-4">
            <th className="col-span-1 text-left font-bold">#</th>
            <th className="col-span-3 text-left font-bold">Plan</th>
            <th className="col-span-2 text-left font-bold">Billing</th>
            <th className="col-span-3 text-left font-bold">Entitlement</th>
            <th className="col-span-1 text-center font-bold">Status</th>
            <th className="col-span-2 text-right font-bold">Actions</th>
          </tr>
        </thead>
        <tbody className="w-full divide-y divide-gray-100 bg-white">
          {isLoading ? (
            <tr className="h-64 w-full">
              <td colSpan={12} className="h-full">
                <div className="flex h-full w-full items-center justify-center">
                  <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#885ABB] border-t-transparent"></div>
                </div>
              </td>
            </tr>
          ) : plans.length === 0 ? (
            <tr className="flex flex-col items-center justify-center h-64 text-gray-400 w-full">
              <td className="w-full h-full flex flex-col items-center justify-center">
                <Search className="w-12 h-12 mb-3 opacity-20" />
                <p className="text-lg font-medium text-gray-500">No membership plans found</p>
              </td>
            </tr>
          ) : (
            plans.map((plan, idx) => (
              <tr
                key={plan.id}
                className="grid grid-cols-12 gap-4 items-center px-6 py-4 hover:bg-purple-50/30 transition-colors bg-white group text-sm w-full"
              >
                <td className="col-span-1 text-gray-500 font-medium">
                  {indexOfFirstItem + idx + 1}
                </td>
                <td className="col-span-3">
                  <p className="font-semibold text-gray-800 group-hover:text-[#885ABB]">
                    {plan.name}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {currencySymbol}
                    {Number(plan.price).toFixed(2)}
                    {plan.rooms?.length
                      ? ` · ${plan.rooms.length} room${plan.rooms.length === 1 ? "" : "s"}`
                      : " · all rooms"}
                  </p>
                </td>
                <td className="col-span-2 text-gray-700">{intervalLabel(plan)}</td>
                <td className="col-span-3 text-gray-700">{entitlementLabel(plan)}</td>
                <td className="col-span-1 text-center">
                  <span
                    className={
                      plan.status === "active"
                        ? "inline-flex rounded-full bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-700"
                        : "inline-flex rounded-full bg-gray-100 px-2 py-0.5 text-xs font-semibold text-gray-600"
                    }
                  >
                    {plan.status}
                  </span>
                </td>
                <td className="col-span-2 flex justify-end gap-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onEdit(plan)}
                    className="text-[#885ABB] hover:bg-purple-50"
                  >
                    <Edit2 size={16} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onDelete(plan.id)}
                    className="text-red-500 hover:bg-red-50"
                  >
                    <Trash2 size={16} />
                  </Button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};
